import { cloneDeep } from 'lodash';
import { TBinaryUpdate } from 'shared/types/domain';

import { createEditor } from 'com/Editor';
import {
  apiAddQuestion,
  apiGetQuestion,
  apiGetTest,
  apiUpdateQuestion,
} from 'core/api';

import Form from './Form';

const editor = createEditor<TTest, TQuestion, TQuestionUpdate>({
  mode: 'withContainer',
  transform,
  Form,
  getParentUid: entity => entity.testUid,
  onValidate: validate,
  genNewEntity: (): TQuestionUpdate => ({
    question: '',
    hint: null,
    options: [''],
  }),
  apiGetContainer: apiGetTest,
  apiGetEntity: apiGetQuestion,
  apiUpdateEntity: apiUpdateQuestion,
  apiAddEntity: apiAddQuestion,
});

function validate(question: TQuestionUpdate | TQuestion) {
  const nonEmptyOptions = question.options.filter(opt => opt.trim());

  if (!question.question.trim() || !nonEmptyOptions.length) {
    return { isEmpty: true, error: null };
  }

  if (!question.question.trim()) {
    return { isEmpty: false, error: 'Question is empty' };
  }

  if (question.options[0].trim() === '') {
    return { isEmpty: false, error: 'Right answer is empty' };
  }

  if (!nonEmptyOptions.length) {
    return {
      isEmpty: false,
      error: 'The question must contain at least 1 option',
    };
  }

  return { isEmpty: false, error: null };
}

function transform(
  question: TQuestion | TQuestionUpdate,
  field: string,
  subField: string | undefined,
  value: string
): TQuestionUpdate {
  switch (field) {
    case 'options': {
      const copy = cloneDeep(question);
      copy.options[Number(subField)] = value;
      while (copy.options[copy.options.length - 1] === '') {
        copy.options.pop();
      }
      return copy;
    }

    case 'resourceBinary': {
      return {
        ...question,
        resourceBinary: value ? (JSON.parse(value) as TBinaryUpdate) : null,
      };
    }

    default:
      // plain field like "note", "valueNote"
      return {
        ...question,
        [field as keyof TQuestion]: value ? value : '',
      };
  }
}

export const QuestionEditorProvider = editor.EditorProvider;
export const useQuestionEditor = function useQuestionEditor() {
  const { createEntity, editEntity } = editor.useEditor();
  return { createQuestion: createEntity, editQuestion: editEntity };
};
